<template>
  <div id="content" class="app-content">
    <!-- page header -->
    <b-row>
      <b-col>
        <h1 class="page-header">Giftcards <small>all listings.</small></h1>
      </b-col>
      <b-col>
        <b-button class="float-right" variant="primary" @click="addGiftcard = !addGiftcard">Add giftcard</b-button>

        <b-button class="float-right mr-2" variant="primary" @click="combineGiftcards = !combineGiftcards"
          >Combine giftcards</b-button
        >
      </b-col>
    </b-row>
    <b-card>
      <b-row v-if="combineGiftcards">
        <b-col class="mb-4">
          <b-input-group v-for="(point, index) in giftcardsToCombine" :key="index" class="mb-3">
            <keep-alive>
              <b-form-input
                :id="`formBulletPointsField${index}`"
                placeholder="23423432234"
                v-model="giftcardsToCombine[index].code"
                style="border-top-right-radius: 0; border-bottom-right-radius: 0; width: 350px"
              />
            </keep-alive>
            <template #append>
              <b-button variant="danger" @click="() => removeBulletPoint(index)">
                <strong>&times;</strong>
              </b-button>
            </template>
          </b-input-group>

          <b-button variant="light" @click="addEmptyBulletPoint">
            Add code
          </b-button>
          <br />
          <b-button class="mt-3" variant="primary" @click="doCombine">
            Combine
          </b-button>
        </b-col>
      </b-row>
      <b-row v-if="addGiftcard">
        <b-col>
          <h3>Add giftcard</h3>
          <form>
            <b-form-group label="Valid to date" label-for="valid-to">
              <b-form-datepicker
                id="valid-to"
                v-model="newGiftcard.valid_to"
                class="mb-2"
                locale="nb-NO"
                :start-weekday="1"
              ></b-form-datepicker>
            </b-form-group>
            <b-form-group label="Giftcard value" label-for="sum">
              <b-form-input id="sum" v-model="newGiftcard.sum" class="mb-2"></b-form-input>
            </b-form-group>
            <b-form-group label="Voucher Code" label-for="voucher-code-id">
              <b-input-group>
                <b-form-input v-model="newGiftcard.code" id="voucher-code-id" placeholder="e.g 52893JD93J" :min="0" />
                <template #append>
                  <b-button variant="success" @click="generateCode">Generate</b-button>
                </template>
              </b-input-group>
              <b-button class="mt-3" variant="primary" type="submit" @click.prevent="saveGiftcard"
                >Create giftcard</b-button
              >
            </b-form-group>
          </form>
        </b-col>
      </b-row>
      <b-row class="mb-4">
        <b-col>
          <input type="text" placeholder="Giftcard ID" class="form-control" v-model="filterById" />
        </b-col>
        <b-col>
          <input type="text" placeholder="Giftcard Code" class="form-control" v-model="filterByCode" />
        </b-col>
      </b-row>

      <b-table ref="table" :fields="fields" :items="giftcardArr">
        <template v-slot:cell(code)="{ value, item }">
          <span>{{ value }}</span>
          <b-badge v-if="voucherStatus(item)" class="ml-2">
            {{ voucherStatusText(item) }}
          </b-badge>
          <b-button variant="primary" v-b-modal="`giftcard-${item.id}`" size="sm" class="ml-2">
            <i class="fa fa-info" />
          </b-button>
          <b-button
            class="ml-2"
            :href="`${rootUrl}/giftcard/${item.id}/download`"
            target="_BLANK"
            variant="primary"
            size="sm"
            title="download voucher"
          >
            <i class="fa fa-download" />
          </b-button>
          <b-modal :id="`giftcard-${item.id}`" title="Giftcard History">
            <giftcard-history :giftcard="item" />
          </b-modal>
        </template>
        <template v-slot:cell(status)="{ value }">
          <b-badge :variant="value ? 'primary' : 'danger'">{{ value ? 'Active' : 'Inactive' }}</b-badge>
        </template>
        <template v-slot:cell(open)="{ value }">
          <b-badge :variant="value ? 'primary' : 'warning'">{{ String(value).toUpperCase() }}</b-badge>
        </template>
        <template v-slot:cell(refundable)="{ value }">
          <b-badge :variant="value ? 'primary' : 'warning'">{{ String(value).toUpperCase() }}</b-badge>
        </template>
        <template v-slot:cell(actions)="{ item }">
          <b-col cols="auto">
            <b-button v-b-modal="`extend-valid-date-${item.id}`" variant="primary" size="sm" title="Extend valid date">
              Extend </b-button
            >&nbsp;

            <b-modal
              title="Extend Valid Date"
              :id="`extend-valid-date-${item.id}`"
              v-slot="{ ok, cancel }"
              @ok="giftcardExtended(item)"
              hide-footer
            >
              <order-detail-date-form :giftcard="item" @cancel="cancel" @updated="giftcardExtended(item)" />
            </b-modal>

            <b-button
              v-if="item.exchanged_at === null && item.type !== 'exchange'"
              v-b-modal="`exchange-giftcard-${item.id}`"
              variant="primary"
              size="sm"
              title="Exchange"
            >
              Exchange </b-button
            >&nbsp;

            <b-modal title="Confirm Exchnage" :id="`exchange-giftcard-${item.id}`" @ok="exchangeGiftcard(item)">
              Are you sure that you want to exchange this giftcard?
              <form>
                <div class="form-group">
                  <label>Enter email of person to recieve the exchanged giftcard PDF</label>
                  <input type="email" v-model="exchangeEmail" class="form-control" />
                </div>
              </form>
            </b-modal>

            <b-button
              v-b-modal="`deactivate-giftcard-${item.id}`"
              variant="danger"
              size="sm"
              title="Deactivate giftcard"
              v-if="item.status === true"
            >
              Deactivate </b-button
            >&nbsp;

            <b-modal title="Confirm deactivation" :id="`deactivate-giftcard-${item.id}`" @ok="deactivateGiftcard(item)">
              Are you sure that you want to deactivate this giftcard?
            </b-modal>

            <b-button
              v-b-modal="`activate-giftcard-${item.id}`"
              variant="primary"
              size="sm"
              title="Deactivate giftcard"
              v-if="item.status === false"
            >
              Activate </b-button
            >&nbsp;

            <b-modal title="Confirm deactivation" :id="`activate-giftcard-${item.id}`" @ok="activateGiftcard(item)">
              Are you sure that you want to activate this giftcard?
            </b-modal>
          </b-col>
        </template>
      </b-table>
      <!-- pagination -->
      <b-pagination
        v-if="meta.total > meta.per_page"
        v-model="meta.current_page"
        :total-rows="meta.total"
        :per-page="meta.per_page"
        @change="paginationChange"
      />
    </b-card>
  </div>
</template>

<script>
import { giftcard } from '@youwish1/api-package'
import { formatDate, formatMoney } from './page-helpers'
import GiftcardHistory from './OrdersPage/GiftcardHistory.vue'
import OrderDetailDateForm from './OrdersPage/OrderDetailDateForm.vue'

export default {
  name: 'GiftcardPage',
  components: { GiftcardHistory, OrderDetailDateForm },
  data() {
    return {
      giftcardArr: [],
      rootUrl: process.env.VUE_APP_API_URL,
      exchangeEmail: '',
      addGiftcard: false,
      combineGiftcards: false,
      giftcardsToCombine: [{ code: '' }, { code: '' }],
      filterById: this.$route.query.id,
      filterByCode: this.$route.query.code,
      newGiftcard: {
        code: '',
        sum: 1000,
        valid_to: null,
      },
      meta: {
        current_page: this.$route.query.page,
        from: 0,
        last_page: 1,
        path: '',
        per_page: 0,
        to: 0,
        total: 0,
      },
    }
  },
  computed: {
    giftcard() {
      return giftcard
    },
    fields() {
      return [
        { key: 'id', label: 'ID', sortable: false },
        { key: 'code' },
        { key: 'sum', formatter: value => formatMoney(value) },
        { key: 'full_sum', formatter: value => formatMoney(value) },
        { key: 'status' },
        { key: 'open' },
        { key: 'refundable' },
        { key: 'type' },
        { key: 'valid_to', formatter: value => formatDate(value) },
        { key: 'actions', label: 'Actions' },
      ]
    },
  },
  async mounted() {
    await this.loadContent()
  },
  methods: {
    async loadContent() {
      const res = await giftcard.go(this.meta.current_page, {
        filter: {
          id: this.$route.query.id,
          code: this.$route.query.code,
        },
      })
      this.giftcardArr = res.data()
      this.meta = res.meta
    },
    generateCode() {
      this.newGiftcard.code = Math.floor(Math.random() * 1e12)
    },
    voucherStatus(giftcard) {
      if (giftcard.refunded_at) return `danger`
      if (giftcard.redeemed_at) return `primary`
      if (Date.now() - Date.parse(giftcard.valid_to) > 0) return `secondary`
    },
    voucherStatusText(giftcard) {
      if (giftcard.refunded_at) return ` Refunded at ${formatDate(giftcard.refunded_at)}`
      if (giftcard.redeemed_at) return ` Redeemed at ${formatDate(giftcard.redeemed_at)}`
      if (Date.now() - Date.parse(giftcard.valid_to) > 0) return ` Expired at ${formatDate(giftcard.valid_to)}`
    },
    async exchangeGiftcard(item) {
      await giftcard.exchange([item.code], this.exchangeEmail)
      this.loadContent()
      this.$bvModal.hide(`exchange-giftcard-${item.id}`)
      this.$bvToast.toast(`Giftcard exchanged`, {
        title: 'Giftcard exchanged',
        variant: 'primary',
      })
    },
    async deactivateGiftcard(item) {
      await giftcard.update(item.id, { status: 0 })
      this.loadContent()
      this.$bvToast.toast(`Giftcard deactivated`, {
        title: 'Giftcard deactivated',
        variant: 'primary',
      })
    },
    async activateGiftcard(item) {
      await giftcard.update(item.id, { status: 1 })
      this.loadContent()
      this.$bvToast.toast(`Giftcard activated`, {
        title: 'Giftcard activated',
        variant: 'primary',
      })
    },
    async saveGiftcard() {
      await giftcard.add({
        valid_to: this.newGiftcard.valid_to,
        code: this.newGiftcard.code,
        sum: this.newGiftcard.sum * 100,
        full_sum: this.newGiftcard.sum * 100,
        original_sum: this.newGiftcard.sum * 100,
        status: 1,
      })

      this.newGiftcard = {
        sum: 1000,
        code: '',
        valid_to: null,
      }

      this.$bvToast.toast(`Giftcard created`, {
        title: 'Giftcard created',
        variant: 'primary',
      })
      this.loadContent()
    },
    giftcardUpdated() {
      this.loadContent()
    },
    removeBulletPoint(index) {
      if (this.giftcardsToCombine.length === 1) {
        return (this.giftcardsToCombine = [{ code: '' }])
      }

      this.giftcardsToCombine.splice(index, 1)
    },
    addEmptyBulletPoint() {
      this.giftcardsToCombine.push({ code: '' })
    },
    async doCombine() {
      await giftcard.combine(this.giftcardsToCombine.map(g => g.code))
      this.$bvToast.toast(`Giftcards combined`, {
        title: 'Giftcards combined',
        variant: 'primary',
      })
      this.loadContent()
    },
    giftcardExtended(item) {
      this.$bvModal.hide(`extend-valid-date-${item.id}`)
      this.$bvToast.toast(`Giftcard extended`, {
        title: 'Giftcard extended',
        variant: 'primary',
      })
      this.loadContent()
    },
    paginationChange(page) {
      const queryObj = {
        page: page,
        id: this.$route.query.id,
        code: this.$route.query.code,
      }
      this.$router.push({ name: 'giftcards', query: queryObj })
      this.meta.current_page = page

      this.loadContent()
    },
  },
  watch: {
    filterById(newVal) {
      this.$router.push({ name: 'giftcards', query: { id: newVal, page: 1 } })
      this.meta.current_page = 1
      this.loadContent()
    },

    filterByCode(newVal) {
      this.$router.push({ name: 'giftcards', query: { code: newVal, page: 1 } })
      this.meta.current_page = 1
      this.loadContent()
    },
  },
}
</script>
